import { PlannerDrawingLabel } from "@/features/drawings/PlannerDrawingPill";
import {
  type NormalizedDrawing,
  type NormalizedDrawingResponse,
} from "@repo/api";
import { Accordion, AccordionItem, LightMode, Text } from "@repo/skapa";
import { t, useTranslation } from "@repo/translation";
import {
  SectionContainer,
  type SelectListItem,
  SelectListView,
  localizeDate,
  localizeTime,
} from "@repo/ui";
import { trackAnalytics } from "@repo/utils";

interface PlannerResultProps {
  searchResult: NormalizedDrawingResponse;
  addedDrawingIds: string[];
  selectedDrawingIds: string[];
  onSelect: (selected: boolean, data: NormalizedDrawing) => void;
  filterAssociated?: boolean;
}

export const PlannerResultList = ({
  searchResult,
  addedDrawingIds,
  selectedDrawingIds,
  onSelect,
  filterAssociated,
}: PlannerResultProps) => {
  const { dateFnsLocale } = useTranslation();

  const getDrawingList = (
    designLists: NormalizedDrawing[]
  ): SelectListItem[] => {
    const filteredDrawingsList = filterAssociated
      ? designLists.filter((item) => !addedDrawingIds.includes(item.id))
      : designLists;

    return filteredDrawingsList.map((el) => ({
      value: el.id,
      disabled: addedDrawingIds.includes(el.id),
      title: <Text>{el.name}</Text>,
      image: el.image,
      description: (
        <>
          <Text>{el.isVPCDrawing ? el.type : el.id}</Text>
          {el.shortcode && (
            <Text>{`${t("label.shortcode")}: ${el.shortcode}`}</Text>
          )}
          <PlannerDrawingLabel drawingInfo={el} />
        </>
      ),
      addOn: (
        <>
          {el.updated && (
            <Text
              tagName="span"
              color={LightMode.colourTextAndIcon3}
              textStyle="Body.S"
            >
              {t("label.updated_at_datetime", {
                date: localizeDate(el.updated, dateFnsLocale),
                time: localizeTime(el.updated, dateFnsLocale),
              })}
            </Text>
          )}
        </>
      ),
    }));
  };
  const sortedPlannerList = [...(searchResult.plannerList ?? [])].sort(
    (a, b) => {
      const aCount = a.designList.filter(
        (item) => !addedDrawingIds.includes(item.id)
      ).length;
      const bCount = b.designList.filter(
        (item) => !addedDrawingIds.includes(item.id)
      ).length;
      if (bCount !== aCount) {
        return bCount - aCount;
      }
      return a.name.localeCompare(b.name);
    }
  );

  return (
    <SectionContainer>
      <Accordion>
        {sortedPlannerList
          .filter((group) => getDrawingList(group.designList).length > 0)
          .map((group) => (
            <AccordionItem
              key={group.name}
              id={group.name}
              title={`${group.name} (${group.designList.length})`}
              open={false}
            >
              <SelectListView
                id="planner-result-list"
                list={getDrawingList(group.designList)}
                selectedKeys={selectedDrawingIds}
                disabledKeys={addedDrawingIds}
                onChange={(selected, item) => {
                  onSelect(
                    selected,
                    group.designList.find(
                      (drawing) => drawing.id === item.value
                    )!
                  );
                  trackAnalytics("select_planner_from_searched_planner_list");
                }}
                selectionMode="MULTI"
              />
            </AccordionItem>
          ))}
      </Accordion>
    </SectionContainer>
  );
};
